import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const RoleClientSelectionContext = createContext({
  selectedRole: null,
  selectedClient: null,
  setSelectedRole: () => {},
  setSelectedClient: () => {},
  setSelection: () => {},
  clearSelection: () => {},
});

function useParsedRoleClient() {
  const location = useLocation();
  const pathParts = location.pathname.split("/");
  if (pathParts.length < 2 || pathParts[1] === "") {
    return { role: null, client: null };
  }

  const roleClientPart = pathParts[1];
  const specialPaths = ["role-selection", "client-selection", "users", "debug"];
  if (specialPaths.includes(roleClientPart.toLowerCase())) {
    return { role: null, client: null };
  }

  const [role, client] = roleClientPart.split("-");
  if (role && client) {
    return { role, client };
  }

  return { role: null, client: null };
}

export const RoleClientSelectionProvider = ({ children }) => {
  const { role: urlRole, client: urlClient } = useParsedRoleClient();
  const [selectedRole, setSelectedRole] = useState(urlRole || null);
  const [selectedClient, setSelectedClient] = useState(urlClient || null);

  // Update state if the URL changes
  useEffect(() => {
    setSelectedRole(urlRole || null);
    setSelectedClient(urlClient || null);
  }, [urlRole, urlClient]);

  const setSelection = (role, client) => {
    setSelectedRole(role);
    setSelectedClient(client);
  };

  const clearSelection = () => {
    setSelectedRole(null);
    setSelectedClient(null);
  };

  const contextValue = {
    selectedRole,
    selectedClient,
    setSelectedRole,
    setSelectedClient,
    setSelection,
    clearSelection,
  };

  return (
    <RoleClientSelectionContext.Provider value={contextValue}>
      {children}
    </RoleClientSelectionContext.Provider>
  );
};

export const useRoleClientSelection = () => {
  const context = useContext(RoleClientSelectionContext);
  if (context === undefined) {
    throw new Error(
      "useRoleClientSelection must be used within a RoleClientSelectionProvider",
    );
  }
  return context;
};

export default RoleClientSelectionContext;
